// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback } from 'react';
import { Checkbox, Label } from '@/components/ui';
import { toggleOrganizationRole } from './org-role-policy';

interface RoleCheckboxesProps {
  roles: string[];
  onChange: (roles: string[]) => void;
  applications: { appId: string; name: string }[];
}

/** Checkbox-based role editor for org membership: admin + per-app workspace_creator roles. */
export function RoleCheckboxes({ roles, onChange, applications }: RoleCheckboxesProps) {
  const isAdmin = roles.includes('admin');

  const toggle = useCallback((role: string) => {
    onChange(toggleOrganizationRole(roles, role));
  }, [roles, onChange]);

  return (
    <div className="space-y-4">
      <div className="-mx-2 flex items-start gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/60">
        <Checkbox
          id="role-admin"
          className="mt-0.5"
          checked={isAdmin}
          onCheckedChange={() => toggle('admin')}
        />
        <div className="grid gap-1">
          <Label htmlFor="role-admin" className="cursor-pointer font-medium">
            Organization admin
          </Label>
          <p className="text-xs text-muted-foreground">
            Administers this organization: workspace creation, user roles, and
            connector administrator grants
          </p>
        </div>
      </div>

      {applications.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Workspace creator roles
            </p>
            {isAdmin && (
              <span className="text-xs italic text-muted-foreground">
                Included with organization admin
              </span>
            )}
          </div>
          {applications.map((app) => {
            const role = `workspace_creator_${app.appId}`;
            const checked = roles.includes(role);
            return (
              <div
                key={app.appId}
                className={`-mx-2 flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors ${
                  isAdmin ? 'opacity-50' : 'hover:bg-muted/60'
                }`}
              >
                <Checkbox
                  id={`role-${role}`}
                  checked={checked || isAdmin}
                  disabled={isAdmin}
                  onCheckedChange={() => toggle(role)}
                />
                <Label
                  htmlFor={`role-${role}`}
                  className={`flex-1 font-normal ${isAdmin ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {app.name}
                </Label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
